import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupee } from "@/lib/constants";
import { HeartPulse, Landmark } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

export const CarePayouts = () => {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/pro/care-earnings").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
    if (!d?.eligible) return null;
    return (
        <section className="mt-6" data-testid="care-payouts-section">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-navy"><HeartPulse size={18} className="text-orange" />Care payouts</h2>
            <div className="grid grid-cols-2 gap-3">
                <Total label="Paid" value={d.paid} tone="text-emerald-700" testid="care-payouts-paid" />
                <Total label="Due" value={d.due} tone="text-amber-700" testid="care-payouts-due" />
            </div>
            <h3 className="mb-2 mt-5 text-sm font-bold text-navy/70">Completed visits & bookings</h3>
            {d.items.length === 0 && <p className="text-sm text-navy/40" data-testid="care-items-empty">No completed visits with a payout yet.</p>}
            <div className="space-y-2">{d.items.map((i) => <Item key={i.id} i={i} />)}</div>
            <h3 className="mb-2 mt-5 text-sm font-bold text-navy/70">Payments received</h3>
            {d.payments.length === 0 && <p className="text-sm text-navy/40" data-testid="care-payments-empty">No payments sent yet.</p>}
            <div className="space-y-2">{d.payments.map((p) => <Payment key={p.id} p={p} />)}</div>
        </section>
    );
};

function Total({ label, value, tone, testid }) {
    return <div className="rounded-2xl border border-navy/10 bg-white p-3.5"><p className="text-xs text-navy/45">{label}</p><p className={`font-heading text-xl font-extrabold ${tone}`} data-testid={testid}>{rupee(value)}</p></div>;
}

function Item({ i }) {
    const paid = i.status === "PAID";
    return (
        <div className="rounded-2xl border border-navy/10 bg-white p-3.5" data-testid={`care-item-${i.id}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-navy">{i.label}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{paid ? "Paid" : "Due"}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-navy/55">
                <span>{fmt(i.date)}{paid && i.ref ? ` · Ref ${i.ref}` : ""}</span>
                <span className="font-heading text-sm font-extrabold text-navy">{rupee(i.amount)}</span>
            </div>
        </div>
    );
}

function Payment({ p }) {
    return (
        <div className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white p-3.5" data-testid={`care-payment-${p.id}`}>
            <div className="rounded-xl bg-emerald-50 p-2"><Landmark size={16} className="text-emerald-700" /></div>
            <div className="flex-1 text-xs text-navy/55">
                <p className="text-sm font-bold text-navy">{rupee(p.amount)} · {p.count} {p.kind === "caregiver" ? "visit" : "booking"}{p.count === 1 ? "" : "s"}</p>
                <p>{fmt(p.at)} · Ref {p.utr}</p>
            </div>
        </div>
    );
}
